import * as Phaser from 'phaser';
import type { Room } from 'colyseus.js';
import {
  MSG_HOME_CONTRIBUTE,
  MSG_HOME_STAGE_UPDATED,
  MSG_HOME_ERROR,
  type HomeContributeRequest,
} from '@game/shared';
import { FontManager } from './FontManager';
import {
  HUD_BG,
  HUD_BORDER,
  HUD_TEXT_PRIMARY,
  HUD_TEXT_SECONDARY,
  PAL_GREEN,
  PAL_YELLOW,
  hexToCSS,
} from './palette';

const HUD_DEPTH = 1000;
const PANEL_X = 8;
const PANEL_Y = 8;
const PANEL_W = 200;
const PANEL_PAD = 8;
const BAR_H = 8;
const BAR_W = PANEL_W - PANEL_PAD * 2;
const ROW_GAP = 22;

const TOAST_FADE_MS = 600;
const TOAST_HOLD_MS = 3000;

type MaterialKey = 'wood' | 'stone' | 'thatch' | 'cloth';
const MATERIAL_KEYS: MaterialKey[] = ['wood', 'stone', 'thatch', 'cloth'];

interface StageThreshold {
  displayName: string | null;
  wood: number;
  stone: number;
  thatch: number;
  cloth: number;
  next: string | null;
}

const STAGES: Record<string, StageThreshold> = {
  foundation:    { displayName: 'A broken foundation.',   wood: 0, stone: 0, thatch: 0, cloth: 0, next: 'broken_hearth' },
  broken_hearth: { displayName: 'A hearth. You could build something on this.', wood: 1, stone: 0, thatch: 0, cloth: 0, next: 'modest_hearth' },
  modest_hearth: { displayName: 'A hearth. Modest.',      wood: 1_000, stone: 100, thatch: 50, cloth: 0, next: 'sleeping_loft' },
  sleeping_loft: { displayName: 'A hearth with a sleeping loft.', wood: 5_000, stone: 500, thatch: 250, cloth: 50, next: 'workroom' },
  workroom:      { displayName: 'A hearth with a sleeping loft and a workroom.', wood: 25_000, stone: 2_500, thatch: 1_000, cloth: 250, next: 'eastern_wing' },
  eastern_wing:  { displayName: 'A hearth with a sleeping loft, a workroom, and an eastern wing.', wood: 100_000, stone: 10_000, thatch: 4_000, cloth: 1_000, next: 'manor' },
  manor:         { displayName: 'The manor.',              wood: 1_000_000, stone: 100_000, thatch: 40_000, cloth: 10_000, next: 'manor_plus' },
  manor_plus:    { displayName: null,                      wood: 10_000_000, stone: 1_000_000, thatch: 400_000, cloth: 100_000, next: null },
};

interface BarRow {
  labelText: Phaser.GameObjects.Text;
  barBg: Phaser.GameObjects.Rectangle;
  barFill: Phaser.GameObjects.Rectangle;
}

export class HomeHUD {
  private scene: Phaser.Scene;
  private room: Room | null = null;
  private container: Phaser.GameObjects.Container;

  private panelBg!: Phaser.GameObjects.Rectangle;
  private stageName!: Phaser.GameObjects.Text;
  private bars = new Map<MaterialKey, BarRow>();
  private contributeBtn!: Phaser.GameObjects.Rectangle;
  private contributeBtnText!: Phaser.GameObjects.Text;

  private currentStage = 'foundation';
  private ledger: Record<string, number> = {};

  private toastText: Phaser.GameObjects.Text | null = null;
  private toastTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0);
    this.container.setDepth(HUD_DEPTH);
    this.container.setScrollFactor(0);
    this.buildPanel();
  }

  attach(room: Room): void {
    this.room = room;

    room.onMessage(MSG_HOME_STAGE_UPDATED, (payload: {
      materialsLedger: Record<string, number>;
      currentStage: string;
      stageAdvanced?: string | null;
    }) => {
      this.ledger = payload.materialsLedger;
      this.currentStage = payload.currentStage;
      this.refresh();
      if (payload.stageAdvanced) {
        this.showToast(`Stage advanced: ${STAGES[payload.stageAdvanced]?.displayName ?? payload.stageAdvanced}`);
      }
    });

    room.onMessage(MSG_HOME_ERROR, (payload: { code: string; message: string }) => {
      this.showToast(payload.message);
    });

    room.state.listen('currentStage', (stage: string) => {
      this.currentStage = stage;
      this.refresh();
    });

    const state = room.state as { currentStage?: string; materialsLedger?: { forEach(cb: (v: number, k: string) => void): void } };
    if (state.currentStage) this.currentStage = state.currentStage;
    if (state.materialsLedger) {
      state.materialsLedger.forEach((v: number, k: string) => {
        this.ledger[k] = v;
      });
    }
    this.refresh();
  }

  private buildPanel(): void {
    const totalRows = MATERIAL_KEYS.length;
    const panelH = PANEL_PAD + 20 + totalRows * ROW_GAP + 30 + PANEL_PAD;

    this.panelBg = this.scene.add.rectangle(
      PANEL_X + PANEL_W / 2, PANEL_Y + panelH / 2,
      PANEL_W, panelH,
      HUD_BG, 0.85,
    );
    this.panelBg.setStrokeStyle(1, HUD_BORDER);
    this.container.add(this.panelBg);

    this.stageName = this.scene.add.text(
      PANEL_X + PANEL_PAD, PANEL_Y + PANEL_PAD,
      'A broken foundation.',
      FontManager.style('hud-sm', {
        color: hexToCSS(HUD_TEXT_PRIMARY),
        wordWrap: { width: PANEL_W - PANEL_PAD * 2 },
      }),
    );
    this.container.add(this.stageName);

    let yOff = PANEL_Y + PANEL_PAD + 20;
    for (const key of MATERIAL_KEYS) {
      const label = this.scene.add.text(
        PANEL_X + PANEL_PAD, yOff,
        `${key}: 0 / 0`,
        FontManager.style('hud-sm', { color: hexToCSS(HUD_TEXT_SECONDARY) }),
      );
      this.container.add(label);

      const barY = yOff + 12;
      const barBg = this.scene.add.rectangle(
        PANEL_X + PANEL_PAD + BAR_W / 2, barY + BAR_H / 2,
        BAR_W, BAR_H,
        HUD_BORDER,
      );
      this.container.add(barBg);

      const barFill = this.scene.add.rectangle(
        PANEL_X + PANEL_PAD, barY + BAR_H / 2,
        0, BAR_H,
        PAL_GREEN,
      );
      barFill.setOrigin(0, 0.5);
      this.container.add(barFill);

      this.bars.set(key, { labelText: label, barBg, barFill });
      yOff += ROW_GAP;
    }

    const btnY = yOff + 4;
    const btnW = PANEL_W - PANEL_PAD * 2;
    const btnH = 22;
    this.contributeBtn = this.scene.add.rectangle(
      PANEL_X + PANEL_PAD + btnW / 2, btnY + btnH / 2,
      btnW, btnH,
      PAL_GREEN, 0.9,
    );
    this.contributeBtn.setStrokeStyle(1, HUD_BORDER);
    this.contributeBtn.setInteractive({ useHandCursor: true });
    this.contributeBtn.on('pointerover', () => this.contributeBtn.setFillStyle(PAL_YELLOW, 1));
    this.contributeBtn.on('pointerout', () => this.contributeBtn.setFillStyle(PAL_GREEN, 0.9));
    this.contributeBtn.on('pointerdown', () => this.onContribute());
    this.container.add(this.contributeBtn);

    this.contributeBtnText = this.scene.add.text(
      PANEL_X + PANEL_PAD + btnW / 2, btnY + btnH / 2,
      'Contribute',
      FontManager.style('hud', { color: '#000000' }),
    );
    this.contributeBtnText.setOrigin(0.5, 0.5);
    this.container.add(this.contributeBtnText);
  }

  private refresh(): void {
    const nextKey = STAGES[this.currentStage]?.next;
    const nextThresh = nextKey ? STAGES[nextKey] : null;
    const currentDisplay = STAGES[this.currentStage]?.displayName ?? this.currentStage;

    this.stageName.setText(currentDisplay);

    for (const key of MATERIAL_KEYS) {
      const row = this.bars.get(key);
      if (!row) continue;

      const contributed = this.ledger[key] ?? 0;
      const required = nextThresh ? nextThresh[key] : 0;

      if (required <= 0) {
        row.labelText.setText(`${key}: ${fmtNum(contributed)}`);
        row.barFill.width = BAR_W;
      } else {
        row.labelText.setText(`${key}: ${fmtNum(contributed)} / ${fmtNum(required)}`);
        const pct = Math.min(1, contributed / required);
        row.barFill.width = Math.max(1, Math.round(BAR_W * pct));
      }
    }

    if (!nextKey) {
      this.contributeBtnText.setText('Complete');
      this.contributeBtn.disableInteractive();
      this.contributeBtn.setAlpha(0.5);
    }
  }

  private onContribute(): void {
    if (!this.room) return;
    const materials: Record<string, number> = {};
    for (const key of MATERIAL_KEYS) {
      const val = this.ledger[key] ?? 0;
      if (val > 0) materials[key] = val;
    }
    const req: HomeContributeRequest = { v: 1, materials };
    this.room.send(MSG_HOME_CONTRIBUTE, req);
  }

  private showToast(message: string): void {
    if (this.toastTween) this.toastTween.destroy();
    if (this.toastText) this.toastText.destroy();

    const cx = this.scene.cameras.main.width / 2;
    const cy = this.scene.cameras.main.height - 40;

    this.toastText = this.scene.add.text(cx, cy, message, FontManager.style('body', {
      color: hexToCSS(PAL_YELLOW),
      shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true, stroke: true },
    }));
    this.toastText.setOrigin(0.5, 0.5);
    this.toastText.setScrollFactor(0);
    this.toastText.setDepth(HUD_DEPTH + 10);
    this.toastText.setAlpha(0);

    this.toastTween = this.scene.tweens.add({
      targets: this.toastText,
      alpha: { from: 0, to: 1 },
      duration: TOAST_FADE_MS,
      ease: 'Sine.easeIn',
      hold: TOAST_HOLD_MS,
      yoyo: true,
      onComplete: () => { this.toastText?.destroy(); this.toastText = null; },
    });
  }

  destroy(): void {
    if (this.toastTween) this.toastTween.destroy();
    if (this.toastText) this.toastText.destroy();
    this.container.destroy(true);
    this.bars.clear();
  }
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
